import { toast } from 'sonner';
import i18next from 'i18next';
import { bumpHubEntityVersion } from '@/lib/reference-sync/bump-hub-entity.js';

export function createTeamAssociationIntegrationMethods({ afterMutate } = {}) {
    return {
        async getRelatedTeams(parentId, params = {}) {
            try {
                const response = await this.service.getRelatedTeams(parentId, params);
                return {
                    success: true,
                    data: response.data || [],
                    pagination: response.pagination || {},
                };
            } catch (error) {
                return { success: false, data: [], pagination: {}, error: error.message };
            }
        },

        async getExecutionPlan(parentId) {
            try {
                const response = await this.service.getExecutionPlan(parentId);
                return { success: true, data: response.data || [] };
            } catch (error) {
                return { success: false, data: [], error: error.message };
            }
        },

        async updateExecutionPlan(parentId, body, opts = {}) {
            try {
                const response = await this.service.updateExecutionPlan(parentId, body);
                // Row-level plan PATCH returns assignments, not the host entity.
                // afterMutate would replace the detail page record and bump the parent
                // hub, which remounts the whole page and jumps scroll to the top.
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.updateSuccess', { defaultValue: 'Assignment updated successfully' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.updateError', { defaultValue: 'Failed to update assignment' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async addTeams(parentId, teamIdsOrBody, opts = {}) {
            try {
                const response = await this.service.addTeams(parentId, teamIdsOrBody);
                afterMutate?.call(this, parentId, response.data);
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.linkSuccess', { defaultValue: 'Teams linked successfully' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.linkError', { defaultValue: 'Failed to link teams' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async copyTeamsFromLinked(parentId, opts = {}) {
            try {
                const response = await this.service.copyTeamsFromLinked(parentId);
                afterMutate?.call(this, parentId, response.data);
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    const addedSources = response.addedSources || [];
                    toast.success(addedSources.length
                        ? i18next.t('teamAssociation:messages.copySuccessMultiSource', {
                            count: addedSources.length,
                            defaultValue: 'Teams copied. {{count}} extra section(s) were kept because the same R&R arrived from more than one source.',
                        })
                        : i18next.t('teamAssociation:messages.copySuccess', { defaultValue: 'Teams copied from linked records' }));
                }
                return { success: true, data: response.data, addedSources: response.addedSources || [] };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.copyError', { defaultValue: 'Failed to copy teams' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async removeTeam(parentId, teamId, opts = {}) {
            try {
                const response = await this.service.removeTeam(parentId, teamId);
                afterMutate?.call(this, parentId, response.data);
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.unlinkSuccess', { defaultValue: 'Team unlinked successfully' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.unlinkError', { defaultValue: 'Failed to unlink team' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async updateAssignment(parentId, assignmentId, assignment, opts = {}) {
            try {
                const payload = Array.isArray(assignment) ? { members: assignment } : assignment;
                const response = await this.service.updateAssignment(parentId, assignmentId, payload);
                // Response is the assignment, not the host — do not patch the detail entity.
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.updateSuccess', { defaultValue: 'Assignment updated successfully' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.updateError', { defaultValue: 'Failed to update assignment' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async removeAssignment(parentId, assignmentId, opts = {}) {
            try {
                const response = await this.service.removeAssignment(parentId, assignmentId);
                afterMutate?.call(this, parentId, response.data);
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.unlinkSuccess', { defaultValue: 'Team unlinked successfully' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.unlinkError', { defaultValue: 'Failed to unlink team' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async getRrLinkCandidates(parentId, params = {}) {
            try {
                const response = await this.service.getRrLinkCandidates(parentId, params);
                return {
                    success: true,
                    data: response.data || [],
                    pagination: response.pagination || {},
                };
            } catch (error) {
                return { success: false, data: [], pagination: {}, error: error.message };
            }
        },

        async addDutyDefinitions(parentId, body, opts = {}) {
            try {
                const response = await this.service.addDutyDefinitions(parentId, body);
                afterMutate?.call(this, parentId, response.data);
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.linkSuccess', { defaultValue: 'Teams linked successfully' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.linkError', { defaultValue: 'Failed to link teams' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async createAndLinkDutyDefinition(parentId, body, opts = {}) {
            try {
                const response = await this.service.createAndLinkDutyDefinition(parentId, body);
                afterMutate?.call(this, parentId, response.data);
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.createDefinitionSuccess', { defaultValue: 'R&R definition created and linked' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.createDefinitionError', { defaultValue: 'Failed to create R&R definition' });
                toast.error(message);
                return { success: false, error: message };
            }
        },

        async removeDutyDefinition(parentId, dutyDefinitionId, opts = {}) {
            try {
                const response = await this.service.removeDutyDefinition(parentId, dutyDefinitionId);
                afterMutate?.call(this, parentId, response.data);
                bumpHubEntityVersion('team');
                if (!opts.silent) {
                    toast.success(i18next.t('teamAssociation:messages.unlinkSuccess', { defaultValue: 'Team unlinked successfully' }));
                }
                return { success: true, data: response.data };
            } catch (error) {
                const message = error.message || i18next.t('teamAssociation:messages.unlinkError', { defaultValue: 'Failed to unlink team' });
                toast.error(message);
                return { success: false, error: message };
            }
        },
    };
}
